import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs';

import type { ApiKeySummary, CreateApiKeyPayload } from '../../models/api-key.models';
import { ApiKeyApiService } from '../../services/api-key-api.service';
import { UserDateTimeFormatService } from '../../services/user-date-time-format.service';
import { normalizeApiError } from '../../utils/error-normalizer';

type ApiKeyEnvironment = 'sandbox' | 'live';

const EXPIRY_DATE_FUTURE_MESSAGE = 'Expiry date must be a future date.';

@Component({
  selector: 'app-api-key',
  imports: [CommonModule, FormsModule],
  templateUrl: './api-key.component.html',
  styleUrl: './api-key.component.scss',
})
export class ApiKeyComponent implements OnInit {
  apiKeys: ApiKeySummary[] = [];
  loading = false;
  errorMessage = '';
  feedbackMessage = '';

  generateModalOpen = false;
  generateModalSubmitting = false;
  generateModalError = '';
  apiKeyName = '';
  environment: ApiKeyEnvironment = 'sandbox';
  tokenValidityEnabled = false;
  expiryDate = '';
  createdTokenValue = '';
  createdTokenName = '';
  copyCreatedTokenPending = false;
  createdTokenCopied = false;
  createdTokenCopyError = '';

  editTarget: ApiKeySummary | null = null;
  editName = '';
  editModalSubmitting = false;
  editModalError = '';

  deleteTarget: ApiKeySummary | null = null;
  deleteSubmitting = false;

  private readonly api = inject(ApiKeyApiService);
  private readonly dateTimeFormat = inject(UserDateTimeFormatService);
  private readonly cdr = inject(ChangeDetectorRef);

  ngOnInit(): void {
    this.loadApiKeys();
  }

  get isSandbox(): boolean {
    return this.environment === 'sandbox';
  }

  keysCountLabel(): string {
    return this.apiKeys.length === 1 ? 'Key' : 'Keys';
  }

  copiedKeyId: string | null = null;
  private copyKeyTimeout: ReturnType<typeof setTimeout> | null = null;

  async copyApiKey(apiKey: ApiKeySummary, event?: MouseEvent): Promise<void> {
    event?.stopPropagation();
    if (!apiKey?.apiKey) {
      return;
    }

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(apiKey.apiKey);
      }
      this.copiedKeyId = apiKey.id;
      if (this.copyKeyTimeout) {
        clearTimeout(this.copyKeyTimeout);
      }
      this.copyKeyTimeout = setTimeout(() => {
        this.copiedKeyId = null;
        this.cdr.markForCheck();
      }, 2000);
    } catch {
      // Fallback
    }
    this.cdr.markForCheck();
  }

  get createdTokenModalOpen(): boolean {
    return this.createdTokenValue.length > 0;
  }

  get canGenerateToken(): boolean {
    if (this.generateModalSubmitting || !this.apiKeyName.trim()) {
      return false;
    }

    if (this.tokenValidityEnabled && (!this.expiryDate || this.isExpiryDateInvalid(this.expiryDate))) {
      return false;
    }

    return true;
  }

  get minExpiryDate(): string {
    return this.shiftIsoDate(this.dateTimeFormat.formatDateInput(new Date()), 1);
  }

  get expiryDateError(): string {
    if (!this.tokenValidityEnabled || !this.expiryDate || !this.isExpiryDateInvalid(this.expiryDate)) {
      return '';
    }

    return EXPIRY_DATE_FUTURE_MESSAGE;
  }

  get editModalOpen(): boolean {
    return this.editTarget !== null;
  }

  get canSaveEdit(): boolean {
    const normalizedName = this.editName.trim().replace(/\s+/g, ' ');
    return Boolean(
      this.editTarget && !this.editModalSubmitting && normalizedName.length > 0 && normalizedName !== this.editTarget.name,
    );
  }

  openGenerateModal(): void {
    this.feedbackMessage = '';
    this.generateModalError = '';
    this.generateModalOpen = true;
  }

  closeGenerateModal(force = false): void {
    if (this.generateModalSubmitting && !force) {
      return;
    }

    this.generateModalOpen = false;
    this.generateModalError = '';
    this.resetGenerateForm();
  }

  setEnvironment(environment: ApiKeyEnvironment): void {
    this.environment = environment;
  }

  setTokenValidity(enabled: boolean): void {
    this.tokenValidityEnabled = enabled;
    if (!enabled) {
      this.expiryDate = '';
      if (this.generateModalError === EXPIRY_DATE_FUTURE_MESSAGE) {
        this.generateModalError = '';
      }
    }
  }

  openEditModal(apiKey: ApiKeySummary): void {
    this.feedbackMessage = '';
    this.editModalError = '';
    this.editTarget = apiKey;
    this.editName = apiKey.name;
  }

  closeEditModal(force = false): void {
    if (this.editModalSubmitting && !force) {
      return;
    }

    this.editTarget = null;
    this.editName = '';
    this.editModalError = '';
  }

  openDeleteConfirm(apiKey: ApiKeySummary): void {
    this.feedbackMessage = '';
    this.deleteTarget = apiKey;
  }

  closeDeleteConfirm(force = false): void {
    if (this.deleteSubmitting && !force) {
      return;
    }

    this.deleteTarget = null;
  }

  closeCreatedTokenModal(): void {
    this.createdTokenValue = '';
    this.createdTokenName = '';
    this.copyCreatedTokenPending = false;
    this.createdTokenCopied = false;
    this.createdTokenCopyError = '';
  }

  async copyCreatedToken(): Promise<void> {
    if (!this.createdTokenValue || this.copyCreatedTokenPending) {
      return;
    }

    this.copyCreatedTokenPending = true;
    this.createdTokenCopied = false;
    this.createdTokenCopyError = '';

    try {
      if (!navigator.clipboard?.writeText) {
        throw new Error('Clipboard unavailable');
      }

      await navigator.clipboard.writeText(this.createdTokenValue);
      this.createdTokenCopied = true;
    } catch {
      this.createdTokenCopyError = 'Unable to copy API key. Please copy it manually.';
    } finally {
      this.copyCreatedTokenPending = false;
      this.cdr.markForCheck();
    }
  }

  submitEdit(): void {
    const target = this.editTarget;
    if (!target || !this.canSaveEdit) {
      return;
    }

    this.editModalSubmitting = true;
    this.editModalError = '';

    this.api
      .updateApiKey(target.id, {
        name: this.editName.trim().replace(/\s+/g, ' '),
      })
      .pipe(
        finalize(() => {
          this.editModalSubmitting = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (updatedApiKey) => {
          this.apiKeys = this.apiKeys.map((apiKey) => (apiKey.id === updatedApiKey.id ? updatedApiKey : apiKey));
          this.feedbackMessage = `API key renamed to ${updatedApiKey.name}.`;
          this.closeEditModal(true);
          this.cdr.markForCheck();
        },
        error: (error: unknown) => {
          this.editModalError = normalizeApiError(error, {
            fallbackMessage: 'Unable to update API key.',
          }).message;
          this.cdr.markForCheck();
        },
      });
  }

  confirmDelete(): void {
    const target = this.deleteTarget;
    if (!target || this.deleteSubmitting) {
      return;
    }

    this.deleteSubmitting = true;
    this.errorMessage = '';

    this.api
      .deleteApiKey(target.id)
      .pipe(
        finalize(() => {
          this.deleteSubmitting = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: () => {
          this.apiKeys = this.apiKeys.filter((apiKey) => apiKey.id !== target.id);
          this.feedbackMessage = `API key deleted for ${target.name}.`;
          this.closeDeleteConfirm(true);
          this.cdr.markForCheck();
        },
        error: (error: unknown) => {
          this.errorMessage = normalizeApiError(error, {
            fallbackMessage: 'Unable to delete API key.',
          }).message;
          this.cdr.markForCheck();
        },
      });
  }

  submitGenerateToken(): void {
    if (this.tokenValidityEnabled && this.isExpiryDateInvalid(this.expiryDate)) {
      this.generateModalError = EXPIRY_DATE_FUTURE_MESSAGE;
      return;
    }

    if (!this.canGenerateToken) {
      return;
    }

    this.generateModalSubmitting = true;
    this.generateModalError = '';

    const payload: CreateApiKeyPayload = {
      name: this.apiKeyName.trim().replace(/\s+/g, ' '),
      environment: this.environment,
    };

    if (this.tokenValidityEnabled) {
      payload.expiresAt = this.expiryDate;
      payload.timeZone = this.dateTimeFormat.preferences().timeZone;
    }

    this.api
      .createApiKey(payload)
      .pipe(
        finalize(() => {
          this.generateModalSubmitting = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (createdApiKey) => {
          this.apiKeys = [createdApiKey, ...this.apiKeys];
          this.feedbackMessage = `API key ${createdApiKey.name} created successfully.`;
          this.createdTokenValue = createdApiKey.plainTextToken;
          this.createdTokenName = createdApiKey.name;
          this.createdTokenCopied = false;
          this.createdTokenCopyError = '';
          this.copyCreatedTokenPending = false;
          this.closeGenerateModal(true);
          this.cdr.markForCheck();
        },
        error: (error: unknown) => {
          this.generateModalError = normalizeApiError(error, {
            fallbackMessage: 'Unable to create API key.',
          }).message;
          this.cdr.markForCheck();
        },
      });
  }

  private resetGenerateForm(): void {
    this.apiKeyName = '';
    this.environment = 'sandbox';
    this.tokenValidityEnabled = false;
    this.expiryDate = '';
  }

  trackByApiKey(_: number, apiKey: ApiKeySummary): string {
    return apiKey.id;
  }

  formatEnvironment(environment: ApiKeyEnvironment): string {
    return environment === 'sandbox' ? 'Sandbox' : 'Live';
  }

  formatDateTime(value: string): string {
    return this.dateTimeFormat.formatDateTime(value);
  }

  private isExpiryDateInvalid(value: string): boolean {
    if (!this.dateTimeFormat.parseDateInput(value)) {
      return true;
    }

    return value < this.minExpiryDate;
  }

  private shiftIsoDate(value: string, days: number): string {
    const parsed = this.dateTimeFormat.parseDateInput(value);
    if (!parsed) {
      return value;
    }

    const shifted = new Date(parsed.getTime() + days * 24 * 60 * 60 * 1000);
    return this.dateTimeFormat.formatDateInput(shifted);
  }

  private loadApiKeys(): void {
    this.loading = true;
    this.errorMessage = '';

    this.api
      .listApiKeys()
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (response) => {
          this.apiKeys = response.items;
          this.cdr.markForCheck();
        },
        error: (error: unknown) => {
          this.errorMessage = normalizeApiError(error, {
            fallbackMessage: 'Unable to load API keys.',
          }).message;
          this.cdr.markForCheck();
        },
      });
  }
}
